/** WPN2 Model controller 
 *    - Javascript playground: https://jsfiddle.net
 * 
*/
// #region WPN2M JS

export function allCategories() {
    return [
        { name: "assets", title: "Assets", entities: ["FixedAsset", "Pipe"], tag: 'assets' },
        { name: "images", title: "Images", entities: ["Image"], tag: 'images' },
        // ALFX_FUTURE: Rename "nhm" | 'nhm' to alfx
        { name: "nhm", title: "ALFX Sensors", entities: ["SoundLocation", "Sensor"], tag: 'nhm' },
        { name: "alfx-heat-map", title: "ALFX Heat Map", entities: ["Sensor"], tag: 'alfx-heat-map' },
        { name: "notes", title: "Notes", entities: ["Note"], tag: 'notes' },
        { name: "work", title: "Work", entities: ["WorkOrder", "Issue", "Activity"], tag: 'work' },
        { name: "support", title: "Support", entities: ["Tutorial"], tag: 'support' },
        { name: "utility", title: "Utility", entities: ["Utility"], tag: 'utility' }
    ];
}

export function mappableEntitiesInSearchOrder() {
    // "FixedAsset", "Pipe", "Sensor", "SoundLocation", "Recording", "Utility", "WorkOrder", "Issue", "Activity"
    const mappableSearchKeys = ["name", "wpn2_type", "address", "entity"];
    return [
        { name: "FixedAsset", searchKeys: mappableSearchKeys.concat(["account_number"]) },
        { name: "Pipe", searchKeys: mappableSearchKeys.concat(["feed", "material", "sub_type"]) },
        { name: "Sensor", searchKeys: mappableSearchKeys.concat(["device_sn", "status", "summary"]) },
        { name: "Recording", searchKeys: mappableSearchKeys },
        { name: "Utility", searchKeys: mappableSearchKeys },
        { name: "WorkOrder", searchKeys: mappableSearchKeys.concat(["priority", "status"]) },
        { name: "Issue", searchKeys: mappableSearchKeys.concat(["priority", "status"]) },
        { name: "Activity", searchKeys: mappableSearchKeys.concat(["statement"]) },
        { name: "SoundLocation", searchKeys: mappableSearchKeys.concat(["status", "summary"]) }
    ];
}

export function allEntities() {
    const nonMappableEntities = [
        { name: "AssetModel", searchKeys: ["name", "wpn2_type"] },
        { name: "Tutorial", searchKeys: ["name", "summary"] }
    ];
    return mappableEntitiesInSearchOrder().concat(nonMappableEntities);
}

export function itemsForCategory(cat, dbData) {
    const catName = cat.name;
    if (catName != null) {
        const entityNames = allCategories().find((x) => x.name === catName)?.entities ?? [];
        if (catName == 'nhm') {
            return entityNames.flatMap((x) => dbData.alfx_data[x] ?? []);
        } else {
            return entityNames.flatMap((x) => dbData.all[x] ?? []);
        }
    }
    return [];
}

export function itemsForCategories(categories, dbData) {
    return categories.flatMap(cat =>
        cat.entities.flatMap(eName =>
          dbData.alfx_data?.[eName] ?? dbData.all?.[eName] ?? []
        )
      );
}

export function isService(item) {
    return ['Service', 'Accessory'].includes(item?.sub_type ?? "");
}

export function allInfrastructures() {
    return [
        { value: "Water", color: "#007AFF", title: "Drinking Water", tag: "water" },
        { value: "Electric", color: "#FF4B4B", title: "Electric", tag: "electric" },
        { value: "Gas", color: "#FFCC00", title: "Gas", tag: "gas" },
        { value: "Storm", color: "#58CAFB", title: "Storm Water", tag: "storm" },
        { value: "Streets", color: "#996633", title: "Streets", tag: "streets" },
        { value: "Waste", color: "#4CD964", title: "Waste Water", tag: "waste" },
    ];
}

export function allMaterials() {
    return [
        { value: "Asbestos Cement", color: "#D2C8BE", title: "Asbestos Cement" },
        { value: "Brick", color: "#F26C4F", title: "Brick" },
        { value: "Cast Iron", color: "#252525", title: "Cast Iron" },
        { value: "Concrete", color: "#7F7F7F", title: "Concrete" },
        { value: "Copper", color: "#FBAF5D", title: "Copper" },
        { value: "Corrugated Steel", color: "#ACACAC", title: "Corrugated Steel" },
        { value: "Ductile Iron", color: "#636363", title: "Ductile  Iron" },
        { value: "Glass Fiber", color: "#2F2F2F", title: "Glass Fiber" },
        { value: "HDP", color: "#343468", title: "HDP" },
        { value: "Lead", color: "#2F2F2F", title: "Lead" },
        { value: "Polyethylene", color: "#393939", title: "Polyethylene" },
        { value: "PVC", color: "#EBEBEB", title: "PVC" },
        { value: "Reinforced Concrete", color: "#E6DCD2", title: "Reinforced Concrete" },
        { value: "Steel", color: "#CCCCCC", title: "Steel" },
        { value: "Vitrified Clay", color: "#7F7F7F", title: "Vitrified Clay" },
        { value: "Wood", color: "#AA7942", title: "Wood" },
        { value: "Unknown", color: "#7F7F7F", title: "Unknown" },
    ];
}

export function allPipeSizes() {
    return [
        { value: "UpToTwoInches", color: "#FF0000", title: "1/4 to 2 in", limit: 2 },
        { value: "UpToFourInches", color: "#FF7F00", title: "3 to 4 in", limit: 4 },
        { value: "UpToSixInches", color: "#00FF00", title: "5 to 6 in", limit: 6 },
        { value: "UpToEightInches", color: "#007FFF", title: "7 to 8 in", limit: 8 },
        { value: "UpToTenInches", color: "#003FFF", title: "9 to 10 in", limit: 10 },
        { value: "UpToTwelveInches", color: "#0000FF", title: "11 to 12 in", limit: 12 },
        { value: "OverTwelveInches", color: "#FF00FF", title: "Over 12 in", limit: 72 },
        { value: "Unknown", color: "#7F7F7F", title: "Unknown", limit: 1000000 }
    ];
}
// #endregion

// #region Associations
/** Items are associated with other items in 5 ways:
        - direct (1 to N): this item directly refers to other items of a particular entity: associations:key, value=[uuid] 
        - indirect (N to 1): other items refer back to this item via their associations
        - parent (1 to N): other_items refer back to this item as their parent
        - child (1 to 1): this item directly refers to one other item as its parent
        - pipe anchor (1 to 1): an anchor has an associated uuid of either another pipe or a fixed asset
*/
export function associatedItemsForEntities(entities, dbData, item, direct=false, indirect=false, parent=false, child=false) {
    var results = entities.flatMap((x) => associatedItemsForEntity(x, dbData ?? [], item, direct, indirect, parent, child));
    return [...new Set(results)];
}

export function firstAssociatedItemForEntities(entities, dbData, item, direct=false, indirect=false, parent=false, child=false) {
    const results = associatedItemsForEntities(entities, dbData, item, direct, indirect, parent, child);
    return (results.length > 0) ? results[0] : {}
}

export function associationKeyForEntity(eName) {
    if (eName === undefined) {
        return undefined;
    }
    return {
        'Activity': 'activity_uuids',
        'Correlation': 'correlation_uuids',
        'FixedAsset': 'fixed_asset_uuids',
        'Pipe': 'pipe_uuids',
        'Image': 'image_uuids',
        'Issue': 'issue_uuids',
        'Note': 'note_uuids',
        'Recording': 'recording_uuids',
        'Sensor': 'sensor_uuids',
        'WorkOrder': 'work_order_uuids'
    }[eName] ?? undefined;
}

function associatedItemsForEntity(eName, dbData, item, direct=false, indirect=false, parent=false, child=false) {
    const eData = dbData[eName] ?? [];
    var associations = [];
    if (direct == true) {
        // 1. Direct associations: Appear in the item's list of associations
        const directAssocKey = associationKeyForEntity(eName);
        if (directAssocKey != undefined) {
            const associatedData = item.associations ?? {};
            const associatedUuids = associatedData[directAssocKey] ?? [];
            const directMatches = associatedUuids.map((x) => firstMatchInItems(eData, 'uuid', x));
            associations = associations.concat(directMatches);
        }
    }
    if (indirect == true) {
        // 2. Indirect associations: Entity records that include the item in their list of associations
        const indirectAssocKey = associationKeyForEntity(item.entity)
        if (indirectAssocKey != undefined) {
            for (let x of eData) {
                const associatedData = x.associations ?? {};
                const associatedUuids = associatedData[indirectAssocKey] ?? [];
                if (associatedUuids.includes(item.uuid)) {
                    associations.push(x);
                }
            }
        }
    }
    if (parent == true) {
        // 3. Parent associations: Entity records that have the item as their parent
        const parentMatches = eData.filter((x) => x.parent_uuid == item.uuid);
        associations = associations.concat(parentMatches);
    }
    if (child == true) {
        // 3. Parent associations: Entity records that have the item as their parent
        const childMatches = eData.filter((x) => x.uuid == item.parent_uuid);
        associations = associations.concat(childMatches);
    }
    const pipeItems  = dbData['Pipe'] ?? [];
    if (item.entity == 'Pipe' && eName == 'Pipe') {
        // 4. Special case of a pipe.
        const anchorUuids = assetUuidsOfPipe(item)
        if (anchorUuids.length > 0) {
            // 4a. The anchors of the pipe item may refer to other pipes or to fixed assets
            const associatedPipes = pipeItems.filter((x) => anchorUuids.includes(x.uuid));
            const fixedAssets = dbData['FixedAsset'] ?? [];
            const associatedFixedAssets = fixedAssets.filter((x) => anchorUuids.includes(x.uuid));
            // 4b. The anchors of other pipes may refer to the pipe item
            const indirectPipes = pipeItems.filter((x) => assetUuidsOfPipe(x).includes(item.uuid));
            associations = associations.concat(associatedPipes, associatedFixedAssets, indirectPipes);
        }
    } else if (item.entity == 'FixedAsset' && eName == 'Pipe') {
        // 5. Special case of a fixed asset: The anchors of pipes may refer to the fixed asset item
        const associatedPipes = pipeItems.filter(x => assetUuidsOfPipe(x).includes(item.uuid));
        associations = associations.concat(associatedPipes);
    }
    return associations.filter(d => Object.keys(d).length > 0);
}

//#region Finding Matching Items
export function firstItemWithValueForKey(key, value, eData) {
    const matches = (key != undefined && value != undefined && eData != undefined) ? eData.filter(x => x[key] == value) : [];
    return (matches != undefined && matches.length > 0) ? matches[0] : undefined;
}

function firstMatchInItems(items, key, value) {
    if (key === undefined) {
        return items?.[0] ?? {};
    }
    return (
        (value != null)
            ? items?.find(item => item[key] === value)
            : items?.find(item => item[key] != null)
    ) ?? {};
}

// #region Mappables: coordinates & addresses
export function coordinateOfMappable(item) {
    if (!item?.lat || !item?.lng) return;
    return new mapkit.Coordinate(item.lat, item.lng);
}

export function coordLines(item, schema) {
    const coordSchema = ["lat", "lng"].map((a) => schema.keysList.find((x) => x.key === a));
    const results = coordSchema.map(x => x.value = textForKeyData(x, item, "Unknown") ?? "");
    const line1 = "Lat: " + results[0];
    const line2 = "Lng: " + results[1];
    return [line1, line2];
}

export function twoLineAddressOfMappable(item) {
    const address = item['address'] ?? {};
    if (address && Object.keys(address).length > 0) {
        const lineOne = item.address["street"] ?? "";
        const city = item.address["city"] ?? "";
        const zip = item.address["postal_code"] ?? ""
        let parts = [];
        if (city) parts.push(city);
        if (zip) parts.push(zip);
        const lineTwo = parts.join(", ");
        if (lineOne || lineTwo) {
            return [lineOne, lineTwo];
        }
        // const addressLines = [lineOne, lineTwo].filter(x => x.length > 0);
        // if (addressLines.length > 0) {
        //     return addressLines;
        // }
    }
}

export function textCoordinateOfMappable(item) {
    textf
    coord = coordinateOfMappable(item)
}
// #endregion

// #region Item Text Checkers
export function summaryForItem(item, truncateLength) {
    switch (item.entity) {
        case "Activity":
        case "Issue":
            const statement = item.statement ?? "";
            return truncatedText(statement, truncateLength);

        case "Sensor":
        case "SoundLocation":
            const summary = item.summary ?? "";
            return truncatedText(summary, truncateLength);

        case "WorkOrder":
            const instructions = item.instructions ?? "";
            return truncatedText(instructions, truncateLength);

        case "Note":
            const noteBody = item.note_body ?? "";
            return truncatedText(noteBody, truncateLength);

        default:
            break;
    }
}

export function briefForItem(item, truncateLength) {
    var itemBrief = "";
    switch (item.entity) {
        case "FixedAsset":
            itemBrief = item.wpn2_type ?? "";
            break;

        case "Pipe":
            const sysText = (item.sub_type != undefined) ? item.sub_type + ": " : "";
            const matText = item.material ?? "";
            const diaText = (item.size != undefined) ? ", " + (item.size / 25.4).toFixed(2) + " in" : "";
            itemBrief = [sysText, matText, diaText].filter(x => x != undefined).join("");
            break;

        case "Sensor":
        case "SoundLocation":
            itemBrief = summaryForItem(item)
            break;

        default:
            break;
    }
    return truncatedText(itemBrief, truncateLength);
}

export function itemUrlForKey(key, item, wpnID, apiUrl) {
    if (item == undefined || key == undefined || wpnID == undefined) {
        return;
    }
    switch (key) {
        case 'image':
            if (item.image_src != undefined) {
                return item.image_src;
            } else if (item.urls != undefined && item.urls['image'] != undefined) {
                return apiUrl + wpnID + "/" + item.urls[key];
    
            } else if (item.uuid != undefined && item.format != undefined) {
                return apiUrl + wpnID + "/WPN2I-" + item.uuid + "." + item.format.toLowerCase()
            }

            default:
            break;
    }
}
// #endregion

// #region Text Matching
export function doesItemMatch(item, sText) {
    if (!item || !sText?.length) return false;
    const entity = allEntities().find(x => x.name === item.entity);
    const searchKeys = entity?.searchKeys ?? [];
    return searchKeys.some(key => doesIncludeText(item, key, sText));
}

function doesIncludeText(item, key, sText) {
    if (item == undefined || item[key] == undefined || sText == undefined || sText.length == 0) {
        return false;
    }
    const sTextUpper = sText.toUpperCase();
    switch (key) {
        case "address":
            const address_keys = ["city", "postal_code", "state", "street"];
            return (address_keys.find((x) => (item.address[x] ?? "").toUpperCase().includes(sTextUpper)) != undefined);

        default:
            return (item[key].toUpperCase().includes(sTextUpper));
    }
}
// #endregion

// #region Properties: Keys, titles & types
export function specificAttributesForItem(item) {
    if (item != undefined) {
        switch (item.entity) {
            case "Activity":
                return ["data_date", "statement"];
            case "FixedAsset":
            case "Pipe":
                return ["account_number", "depth", "features", "gis_id", "profile", "units",
                    "area", "basin", "cover", "discharge", "filter", "grate", "pump", "serial_number",
                    "base", "casing", "connection", "feed", "flow_direction", "lining", "pressure", "sound_velocity", "thickness"
                ];
            case "Issue":
                return ["statement"];
            case "WorkOrder":
                return ["instructions"];
            case "NoiseSource":
            case "Recording":
            case "Sensor":
                // TODO: Implement
                return [];
            default:
                break;
        }
    }
}

export function propertiesForActivityItem(item) {
    if (item != undefined && item.entity != undefined && item.entity == "Activity") {
        // TODO: Make a properties method
        switch (item.wpn2_type) {
            case "Backflow Prevention":
                return ["Tester #", "Test Kit", "Backflow Assembly",
                    "Tightness CV1", "Tightness CV2", "Tightness RV", "PressureCV1", "PressureCV2", "PressureRV",
                    "Tightness SV1", "Tightness SV2",
                ];

            case "CPT Reading":
                return ["Pipe-Soil Potential", "Continuity Test"];

            case "Valve Exercising":
                return ["Final Position", "Turns to Open", "Direction", "Torque", "Cycles"];

            case "Hydrant Flushing":
                return ["Completed", "Flow Rate", "Duration", "Pressure Up", "Pressure Down",
                    "Chlorine Before", "Chlorine After", "pH", "Turbidity", "Leak Sound"
                ];

            case "Leak":
                return ["Completed", "Repair Date", "Leak Type", "Flow Rate"];

            case "Meter Data":
                return ["Old Model", "New Model", "Old SN", "New SN", "Reading", "Has Sound", "Continuous Flow"];

            case "Water Quality":
                return ["Water Aesthetics", "MCL"];

            case "Inspection":
            case "Installation":
            case "Maintenance":
            case "Painting":
            case "Pipe Lining":
            case "Street Cleaning":
            default:
                break;
        }
    }
    return [];
}
// #endregion

// #region Pipes
export function coordinatesOfPipe(item) {
    const anchors = item?.anchors ?? [];
    const coordsForAnchors = anchors.map(x => coordinateOfMappable(x));
    const coords = coordsForAnchors.filter(c => c != undefined);
    return (coords.length >= 2) ? coords : [];
}

export function connectionCoordinatesOfPipe(item) {
    const anchors = item?.anchors ?? [];
    const connectionAnchors = anchors.filter(x => x.asset_uuid != undefined);
    const coordsForAnchors = connectionAnchors.map(x => coordinateOfMappable(x));
    return coordsForAnchors.filter(x => x != undefined);
}

export function assetUuidsOfPipe(item) {
    const anchors = item?.anchors ?? [];
    const connectionAnchors = anchors.filter(x => x.asset_uuid != undefined);
    return connectionAnchors.map(x => x.asset_uuid);
}
// #endregion

export function textForKeyData(keyData, item, defaultValue) {
    if (keyData == undefined || keyData.key == undefined || item == undefined) {
        return defaultValue;
    }
    const key = keyData.key;
    const keyType = keyData.type ?? "string";
    const keyCat = keyData.category ?? "";
    const keyValue = item[key];
    // Types:      string, int, double, bool, array
    // Categories: address, coordinate, date, id, torque, money, pressure, velocity, distance, small_distance, minutes, years, flow_rate
    if (keyValue == undefined || keyValue.length == 0) {
        return defaultValue;
    } else if (typeof keyValue === 'boolean' || keyValue instanceof Boolean) {
        return (keyValue == true) ? "yes" : "no";
    } else if (keyCat == "coordinate") {
        return  keyValue.toFixed(7);
    } else if (keyCat == "date") {
        return dateTextforTimeMS(keyValue) ?? "";
    } else if (keyCat == "minutes") {
        return (keyValue / 60).toFixed(2) + " mins";
    } else if (keyCat == "years") {
        return (keyValue / (86400 * 365)).toFixed(0) + " years";
    } else if (keyCat == "strings") {
        return keyValue.join(", ");
    } else if (keyCat == "flow_rate") {
        return (keyValue / 3.78541).toFixed(2) + " gpm";
    } else if (keyCat == "pressure") {
        return (keyValue / 0.0689476).toFixed(2) + " psi";
    } else if (keyCat == "chlorine") {
        return keyValue.toFixed(2) + " mg/l";
    } else if (keyCat == "voltage") {
        return keyValue.toFixed(2) + " Volts";
    } else if (keyCat == "torque") {
        return (keyValue * 0.737562149).toFixed(1) + "lbf·ft";
    } else if (keyCat == "turbidity") {
        return keyValue.toString() + " NTU";
    } else if (keyCat == "distance") {
        return (keyValue * 3.28084).toFixed(2) + " ft";
    } else if (keyCat == "small_distance") {
        return (keyValue / 25.4).toFixed(2) + " in";
    } else if (keyCat == "velocity") {
        return (keyValue * 3.28084).toFixed(2) + " ft/s";
    } else if (keyCat == "money") {
        return "$" + keyValue.toString();
    } else if (keyType == "double") {
        return keyValue.toFixed(0);
    } else if (keyType == "int") {
        return keyValue.toString();
    } else if (Object.prototype.toString.call(keyValue) === "[object String]") {
        return keyValue;
    }
    return defaultValue;
}

function truncatedText(text, maxLength) {
    const isTextLong = (text != undefined && text.length > maxLength);
    return (isTextLong) ? text.substr(0, (maxLength - 1)) + ".." : text;
}

// #region Dates
export function dateTextforTimeMS(timeMS, isCompact) {
    if (timeMS != undefined) {
        const options = (isCompact == true) ? { year: '2-digit', month: '2-digit', day: '2-digit' } : { year: 'numeric', month: 'short', day: 'numeric' };
        let d = new Date(timeMS);
        return d.toLocaleDateString(undefined, options);
    }
    return "";
}
// #endregion

export function createdItemWithEntity(entity, name, wpn2_type, parent, seed) {
    // Create a root item
    var createdItem = createdRootItem(entity, name, wpn2_type, parent, seed);
    switch (entity) {        
        case 'Feedback':
            createdItem['leak_type'] = "Unknown";
            createdItem['sound_source'] = "Unknown";
            break;

        case 'Image':
            // Add format and [optionally] image data externally
            createdItem['author_name'] = seed.user_item?.name ?? "Unknown";
            break;

        case 'Note':
            // Add note_body externally
            createdItem['author_uuid'] = seed.user_item?.uuid ?? "0";
            createdItem['editor_uuid'] = seed.user_item?.uuid ?? "0";
            break;
    
        default:
            createdItem =  {};
            break;
    }
    return createdItem;
}

function createdRootItem(entity, name, wpn2_type, parent, seed) {
    const creationTimestamp = Date.now();
    const originTime = new Date(2012, 4, 1, 0, 0, 0, 0).getTime();
    const deltaID = creationTimestamp - originTime;
    const f8 = (Math.random() * 1.0).toFixed(6);
    const f6 = f8.substring(f8.length - 6);
    const uuid = deltaID.toString() + f6;
    return {
        'entity': entity, 
        'last_edited_uuid': seed.user_item?.uuid ?? "0",
        'last_updated': creationTimestamp, 
        'name': name ?? nextAvailableName(entity, dbData),
        'parent_uuid': parent?.uuid ?? seed.utility_item?.uuid ?? "0",
        'source': "WPN64",
        'utility_uuid': seed.utility_item?.uuid ?? "0",
        'uuid': uuid,
        'wpn2_type': wpn2_type
    };
}

// TODO: Implement like Swift code, with stem taken from existing recent data
export function nextAvailableName(entity, dbData) {
    return "";
}

export function authorForItem(item, dbData) {
    return firstItemWithValueForKey("uuid", item.author_uuid, dbData['Person']) ?? {};
}
